import { useState, useEffect, useRef } from 'react';
import { C } from '../../lib/tokens.js';
import { t } from '../../lib/data.js';
import { apiFetch } from '../../lib/api.js';
import { invalidateAvatar } from '../../lib/selfUsers.js';
import { getDefaultView, setDefaultView } from '../../lib/viewPref.js';
import { Button } from '../../components/ui/Button.jsx';
import { SectionCard } from '../../components/ui/SectionCard.jsx';
import { PageShell } from '../../components/layout/PageShell.jsx';
import { Avatar } from '../../components/ui/Avatar.jsx';
import { PageSpinner } from '../../components/ui/Misc.jsx';
import { ApiTokensSection } from './ApiTokensSection.jsx';

function ProfileField({ label, value, empty }) {
  return (
    <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${C.mercury}`, padding: '10px 0', alignItems: 'baseline' }}>
      <span style={{ width: 110, flex: 'none', fontSize: 12, color: C.iron, fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 13, color: empty ? C.silver : C.fg, fontStyle: empty ? 'italic' : 'normal' }}>
        {value || (empty ? '—' : '')}
      </span>
    </div>
  );
}

const MAX_AVATAR_BYTES = 2 * 1024 * 1024;
const AVATAR_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const selectStyle = {
  fontFamily: 'Roboto', fontSize: 13, padding: '6px 8px', borderRadius: 4, border: `1px solid ${C.mercury}`,
  background: C.platinum, color: C.fg, outline: 'none', cursor: 'pointer', maxWidth: '100%',
};

// One preference row: label + help on the left, the control on the right (stacked on a phone).
function PrefRow({ label, help, status, children, last }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '12px 0', borderBottom: last ? 'none' : `1px solid ${C.mercury}` }}>
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        <div style={{ fontSize: 13, color: C.fg }}>{label}</div>
        <div style={{ fontSize: 11, color: C.iron, marginTop: 2 }}>{help}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {status ? <span style={{ fontSize: 11, color: status.ok ? C.patina : C.rust }}>{status.text}</span> : null}
        {children}
      </div>
    </div>
  );
}

export function AccountPage() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [prefStatus, setPrefStatus] = useState({});
  const [defaultView, setDefaultViewState] = useState(getDefaultView());
  const fileRef = useRef(null);

  useEffect(() => {
    apiFetch('/me')
      .then(data => {
        setMe({
          name: data.name ?? 'User',
          initials: data.initials ?? '',
          avatar_url: data.avatar_url ?? null,
          role: data.kind ?? data.role ?? 'customer',
          email: data.email ?? '',
          company: data.company ?? '',
          address: data.address ?? '',
          city: data.city ?? '',
          country: data.country ?? '',
          phone: data.phone ?? '',
          locale: data.locale ?? 'en-US',
          notificationType: data.notification_type ?? 'E',
          canManageApiTokens: data.can_manage_api_tokens ?? false,
        });
      })
      .catch(() => {
        setMe({ name: 'Error Loading Profile', initials: '??', role: 'customer' });
      })
      .finally(() => setLoading(false));
  }, []);

  async function onPickFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarError('');
    // Quick checks for a clear message; the server checks the size and the real type again.
    if (!AVATAR_TYPES.includes(file.type)) { setAvatarError(t('account.avatar.bad_type')); return; }
    if (file.size > MAX_AVATAR_BYTES) { setAvatarError(t('account.avatar.too_big')); return; }
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiFetch('/me/avatar', { method: 'POST', body: fd });
      const path = res?.avatar_url ?? me.avatar_url;
      invalidateAvatar(path);
      setMe(prev => ({ ...prev, avatar_url: path }));
    } catch (err) {
      setAvatarError(err.status === 400 && err.message ? err.message : t('account.avatar.failed'));
    } finally {
      setAvatarBusy(false);
    }
  }

  async function removeAvatar() {
    setAvatarError('');
    setAvatarBusy(true);
    try {
      await apiFetch('/me/avatar', { method: 'DELETE' });
      invalidateAvatar(me.avatar_url);
      setMe(prev => ({ ...prev, avatar_url: null }));
    } catch {
      setAvatarError(t('account.avatar.failed'));
    } finally {
      setAvatarBusy(false);
    }
  }

  async function savePref(key, body, apply, after) {
    try {
      await apiFetch('/me', { method: 'PATCH', body: JSON.stringify(body) });
      apply();
      setPrefStatus(prev => ({ ...prev, [key]: { ok: true, text: t('account.pref.saved') } }));
      if (after) after();
    } catch {
      setPrefStatus(prev => ({ ...prev, [key]: { ok: false, text: t('account.pref.save_failed') } }));
    }
  }

  if (loading) {
    return (
      <PageShell title={t('account.title')}>
        <PageSpinner label="Loading profile…" />
      </PageShell>
    );
  }

  return (
    <PageShell title={t('account.title')}>
      {/* Two columns on a wide screen (identity card + settings), stacked on a phone */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start', maxWidth: 1100 }}>

        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
          <SectionCard title={t('account.profile')}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center' }}>
              <Avatar user={me} size={96} />
              <div style={{ fontSize: 17, fontWeight: 500, color: C.deepsea, marginTop: 8 }}>{me.name}</div>
              <span style={{
                display: 'inline-block', fontSize: 11, fontWeight: 500,
                background: me.role === 'agent' ? C.liquid : C.sky,
                color: me.role === 'agent' ? '#fff' : C.deepsea,
                padding: '2px 8px', borderRadius: 99, textTransform: 'capitalize',
              }}>{me.role}</span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginTop: 10 }}>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onPickFile} style={{ display: 'none' }} />
                <Button kind="outline" size="sm" icon="photo_camera" disabled={avatarBusy} onClick={() => fileRef.current?.click()}>
                  {me.avatar_url ? t('account.avatar.replace') : t('account.avatar.upload')}
                </Button>
                {me.avatar_url ? <Button kind="ghost" size="sm" icon="delete" disabled={avatarBusy} onClick={removeAvatar}>{t('account.avatar.remove')}</Button> : null}
              </div>
              <div style={{ fontSize: 11, color: C.iron }}>{t('account.avatar.hint')}</div>
              {avatarError ? <div role="alert" style={{ fontSize: 12, color: C.rust }}>{avatarError}</div> : null}
            </div>

            <div style={{ borderTop: `1px solid ${C.mercury}`, marginTop: 18 }}>
              <ProfileField label={t('account.field.email')}   value={me.email} empty={!me.email} />
              <ProfileField label={t('account.field.company')} value={me.company} empty={!me.company} />
              <ProfileField label={t('account.field.phone')}   value={me.phone}   empty={!me.phone} />
            </div>

            <div style={{ marginTop: 14, padding: '10px 12px', background: C.platinum, borderRadius: 4, fontSize: 12, color: C.iron }}>
              <span className="material-icons" style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 6, color: C.silver }}>info_outline</span>
              {t('account.profile.note')}
            </div>
          </SectionCard>
        </div>

        <div style={{ flex: '2 1 420px', minWidth: 0 }}>
          <SectionCard title={t('account.preferences')}>
            <PrefRow label={t('account.pref.language')} help={t('account.pref.language.help')} status={prefStatus.language}>
              <select value={me.locale} style={selectStyle}
                onChange={e => { const v = e.target.value; savePref('language', { locale: v }, () => setMe(prev => ({ ...prev, locale: v })), () => window.location.reload()); }}>
                <option value="en-US">English</option>
                <option value="nl-NL">Nederlands</option>
              </select>
            </PrefRow>
            <PrefRow label={t('account.pref.notifications')} help={t('account.pref.notifications.help')} status={prefStatus.notifications}>
              <select value={me.notificationType} style={selectStyle}
                onChange={e => { const v = e.target.value; savePref('notifications', { notification_type: v }, () => setMe(prev => ({ ...prev, notificationType: v }))); }}>
                {['E', 'N', 'B', 'X'].map(v => <option key={v} value={v}>{t(`account.pref.notify.${v}`)}</option>)}
              </select>
            </PrefRow>
            <PrefRow last label={t('account.pref.view')} help={t('account.pref.view.help')} status={prefStatus.view}>
              <select value={defaultView} style={selectStyle}
                onChange={e => { const v = e.target.value; setDefaultView(v); setDefaultViewState(v); setPrefStatus(prev => ({ ...prev, view: { ok: true, text: t('account.pref.saved') } })); }}>
                <option value="board">{t('req.view.board')}</option>
                <option value="timeline">{t('req.view.timeline')}</option>
              </select>
            </PrefRow>
          </SectionCard>

        <SectionCard title={t('account.security')}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: C.fg }}>{t('account.security.password')}</div>
            <span style={{ fontSize: 12, color: C.silver, fontStyle: 'italic' }}>Coming soon</span>
          </div>
          <form onSubmit={e => e.preventDefault()}
            style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 320 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: C.iron, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t('account.field.current_password')}
              </label>
              <input
                type="password" name="current-password" autoComplete="current-password"
                value={currentPw} onChange={e => setCurrentPw(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: `1px solid ${C.mercury}`, borderRadius: 4, background: C.platinum, color: C.fg, fontFamily: 'Roboto', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: C.iron, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t('account.field.new_password')}
              </label>
              <input
                type="password" name="new-password" autoComplete="new-password"
                value={newPw} onChange={e => setNewPw(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: `1px solid ${C.mercury}`, borderRadius: 4, background: C.platinum, color: C.fg, fontFamily: 'Roboto', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: C.iron, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {t('account.field.confirm_password')}
              </label>
              <input
                type="password" name="confirm-password" autoComplete="new-password"
                value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: `1px solid ${C.mercury}`, borderRadius: 4, background: C.platinum, color: C.fg, fontFamily: 'Roboto', boxSizing: 'border-box' }}
              />
            </div>
            {confirmPw && newPw !== confirmPw && (
              <div style={{ fontSize: 12, color: C.rust }}>{t('account.password_mismatch')}</div>
            )}
            <div>
              <Button kind="primary" disabled>
                {t('account.save')}
              </Button>
            </div>
          </form>
        </SectionCard>

        {/* Only for roles with "Can Manage API Tokens" (AFSP_PortalPageAccess) */}
        {me.canManageApiTokens && <ApiTokensSection />}

        </div>
      </div>
    </PageShell>
  );
}
